'use client'
import { useState, useEffect, useCallback } from 'react';
import { generateSuccessStory } from './actions';
import { Button } from '@/components/ui/button';
import { Badge } from "@/components/ui/badge";
import { CircleQuestionMark, Copy, Save } from 'lucide-react';
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { useRouter } from "next/navigation";
import CartaTokens from '@/../public/images/tokens_sm.png';
import Image from 'next/image';
import { getActionsCost } from '../new-resume/actions';
import { Loader2, Sparkles } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Link from 'next/link';
import LoginRedirect from '@/components/LoginRedirect';
import AdSterraIFrameBannerAuto from '@/components/adsterra-ui/adsterra-ifram-auto';
import AdSterra320x50 from '@/components/adsterra-ui/adsterra_320x50';
import AdSterraNativeBanner from '@/components/adsterra-ui/adsterra-native-banner';

type Question = {
  question: string;
  why: string;
  tip: string;
};

type TStory = {
  Situation: string,
  Task: string,
  Action: string,
  Result: string
}

function SuccessStoryWrapper() {
  const [briefStory, setBriefStory] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TStory | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [ storyCost, setStoryQCost ] = useState<number>(0);
  const [ isLoadingCost, setIsLoadingCost ] = useState<boolean>(true);

  const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
  const router = useRouter();

  useEffect(() => {
    async function getActions () {
      setIsLoadingCost(true);

      let a = await getActionsCost('SUCCSTRY01');
      console.log(a.data);
      setStoryQCost(a.data);
      setIsLoadingCost(false);
    }

    getActions();

  }, []);

  useEffect(() => {    
    if (!isLoading && isAuthenticated) {
      setUserId(user!.id);

    } else if(!isLoading && !isAuthenticated) {
        router.push("/api/auth/login");
    }
  }, [ isLoading, isAuthenticated ])

  const handleGenerate = async () => {
    setLoading(true);
    // Simulate API Call
    if (!briefStory) return;
    setLoading(true);
    
    try {
      const aiResults = await generateSuccessStory(briefStory, "userId"); // Calling the Server Action
      // force user tokens balance component to refresh
		  window.dispatchEvent(new Event('tokens-deducted'));
      
      setResults(aiResults!);
    } catch (error) {
      console.error("AI Generation failed:", error);
      alert("Something went wrong with the AI.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = useCallback(() => {
    if (results) {
      const successStory = "Situation: " + results.Situation + '\n'
        + "Task: " + results.Task + '\n'
        + "Action: " + results.Action + '\n'
        + "Result: " + results.Result;

      navigator.clipboard.writeText(successStory).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Reset success message after 2s
      });
    }
  }, [ results ]);

  return (
    <>
      <div className="space-y-8 max-w-7xl mx-auto">
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Success Story Rewriter"}</span>
        </div>
        
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Success Story Rewriter
        </h1>

        <AdSterraIFrameBannerAuto position='top' />

        <p className="text-gray-600 dark:text-gray-400">
          Let us help you rewrite your success story...
        </p>

        <div className='pt-4 flex flex-col space-y-4'>
          <label className='text-indigo-600 ml-3 font-medium italic'>In few sentences, tell us about a problem you solved. Focus on the challenge or "pain point."</label>
          <div className="grid grids-rows gap-8">
            {/* Left Side: Input */}
            <div className="flex flex-col space-y-4">
                <textarea
                className="w-full h-32 p-4 bg-gray-50 rounded-xl border-none outline-none
                          shadow-[inset_4px_4px_8px_rgba(0,0,0,0.1),inset_-4px_-4px_8px_rgba(255,255,255,1)]
                          focus:ring-2 focus:ring-blue-400 transition-all"
                placeholder="Paste the your short story here..."
                value={briefStory}
                onChange={(e) => setBriefStory(e.target.value)}
              />
              <button
                onClick={handleGenerate}
                disabled={loading || !briefStory}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              >
                {
                  loading ? 'Analyzing Story...' 
                  : (
                      <div className="flex justify-center items-center gap-2">
                        <div className="flex justify-center items-center">
                          <Sparkles className="mr-2 w-4 h-4" /> 
                          Rewrite Success Story
                        </div>
                        <div className="ml-2 flex justify-center items-center text-sm">
                          <span>{"("}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                          <span className="ml-1">
                            {
                              isLoadingCost 
                              ? <Loader2 size={32} className="animate-spin" /> 
                              : storyCost
                            } 
                            <span > tokens</span>
                          </span><span>{")"}</span> {/* className="hidden md:inline" */}
                        </div>
                      </div>
                    )
                }
              </button>
            </div>

            <hr />

            <AdSterra320x50 />

            <hr />

            {/* Right Side: Results */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 h-auto">
              {!results ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                  <span className="text-5xl mb-4">🏆</span>
                  <p>Your Success Story is Rewritten Here...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <span className='text-2xl font-medium text-indigo-600'>🏆 Your Succes Story Rewritten</span>
                  </div>
                    <Accordion
                        type="single"
                        collapsible
                        className="w-full mr-2 md:mr-0"
                    >
                      <AccordionItem value={"Situation"}>
                        <AccordionTrigger className="text-sm pl-2 bg-gray-200">
                          <div className="flex flex-row gap-2 items-start">
                            Situation
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="text-sm space-y-3">
                            <p className='mt-2'><span className="text-gray-500 font-medium">{results.Situation}</span></p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value={"Task"}>
                        <AccordionTrigger className="text-sm pl-2 bg-gray-200">
                          <div className="flex flex-row gap-2 items-start">
                            Task
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="text-sm space-y-3">
                            <p className='mt-2'><span className="text-gray-500 font-medium">{results.Task}</span></p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value={"Action"}>
                        <AccordionTrigger className="text-sm pl-2 bg-gray-200">
                          <div className="flex flex-row gap-2 items-start">
                            Action
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="text-sm space-y-3">
                            <p className='mt-2'><span className="text-gray-500 font-medium">{results.Action}</span></p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value={"Result"}>
                        <AccordionTrigger className="text-sm pl-2 bg-gray-200">
                          <div className="flex flex-row gap-2 items-start">
                            Result
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="text-sm space-y-3">
                            <p className='mt-2'><span className="text-gray-500 font-medium">{results.Result}</span></p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value={"All"}>
                        <AccordionTrigger className="text-sm pl-2 bg-gray-200">
                          <div className="flex flex-row gap-2 items-start">
                            Full Story
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="text-sm space-y-3">
                            <p className='mt-2'>
                              <span className="text-gray-500 font-medium">
                                <span className='font-bold mr-2'>Situation:</span>{results.Situation}
                              </span>
                            </p>
                            <p className='mt-2'>
                              <span className="text-gray-500 font-medium">
                                <span className='font-bold mr-2'>Task:</span>{results.Task}
                              </span>
                            </p>
                            <p className='mt-2'>
                              <span className="text-gray-500 font-medium">
                                <span className='font-bold mr-2'>Action:</span>{results.Action}
                              </span>
                            </p>
                            <p className='mt-2'>
                              <span className="text-gray-500 font-medium">
                                <span className='font-bold mr-2'>Result:</span>{results.Result}
                              </span>
                            </p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    <div className="flex justify-end mt-4 space-x-3">
                      <Button 
                          variant="outline"
                          onClick={handleCopy}
                          disabled={!results}
                          className="flex items-center space-x-2"
                      >
                          <Copy className="h-4 w-4" />
                          <span>{copySuccess ? 'Copied!' : 'Copy to Clipboard'}</span>
                      </Button>
                      
                      <Button className="bg-sky-500 hover:bg-sky-600 text-white flex items-center space-x-2" 
                        disabled={!results || !userId}
                        // onClick={handleSaveDraft}
                      >
                          <Save className="h-4 w-4" />
                          <span>Save Draft</span>
                      </Button>
                      {/* </form> */}
                    </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='w-full flex justify-center items-center mt-8'>
        <AdSterraNativeBanner />
      </div>

      {/* 2. THE OVERLAY (Only shows if not logged in) */}
      {(!isAuthenticated && !isLoading) && (
        <LoginRedirect message="to analyze this job description" />
      )}
    </>
  );
}

function QuestionCard({ data }: { data: Question }) {
  return (
    <div className="mb-4 p-4 rounded-lg border border-gray-100 bg-gray-50 hover:border-blue-200 transition-all">
      <h4 className="font-bold text-gray-800 mb-2">"{data.question}"</h4>
      <div className="text-sm space-y-1">
        <p><span className="text-gray-500 font-medium">The "Why":</span> {data.why}</p>
        <p className="text-green-700"><span className="font-medium">💡 Tip:</span> {data.tip}</p>
      </div>
    </div>
  );
}

export default SuccessStoryWrapper
